import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { PerimetersService } from './perimeters.service';
import { YearsService } from './years.service';

export interface RoadmapNotationObservableMap<T extends KPI.BaseRoadmapNotation[]> {
  [key: string]: Observable<T>;
}

export interface RoadmapNotationLoadingMap {
  [key: string]: ReplaySubject<boolean>;
}

@Injectable()
export class RoadmapNotationsService {

  private roadmapIndicatorsNotations: RoadmapNotationObservableMap<KPI.RoadmapIndicatorNotation[]> = {};
  private roadmapIndicatorsNotationsLoading: RoadmapNotationLoadingMap = {};

  private chaptersNotations: RoadmapNotationObservableMap<KPI.ChapterNotation[]> = {};
  private chaptersNotationsLoading: RoadmapNotationLoadingMap = {};

  private controlPointsNotations: RoadmapNotationObservableMap<KPI.ControlPointNotation[]> = {};
  private controlPointsNotationsLoading: RoadmapNotationLoadingMap = {};

  private notationsRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
    private yearsService: YearsService,
  ) { }

  public refreshNotations(): void {
    this.notationsRefresh$.next();
  }

  public getRoadmapIndicatorsNotations(
    forcedPerimeters?: KPI.Perimeter[],
    forcedYear?: number,
  ): [Observable<KPI.RoadmapIndicatorNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.roadmapIndicatorsNotations[key]) {
      this.roadmapIndicatorsNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.RoadmapIndicatorNotation[] = [];

      this.roadmapIndicatorsNotations[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.yearsService.startYear$,
        this.notationsRefresh$,
      ).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.roadmapIndicatorsNotationsLoading[key].next(true)),
        switchMap(([perimeters, startYear]) =>
          this.api.roadmap_notations_roadmap_indicators(
            {
              year: forcedYear || startYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
            },
          ).pipe(
            catchError(this.handleError),
            finalize(() => this.roadmapIndicatorsNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.roadmapIndicatorsNotations[key], this.roadmapIndicatorsNotationsLoading[key]];
  }

  public getRoadmapIndicatorNotation(
    roadmapIndicator: number,
    forcedPerimeters?: KPI.Perimeter[],
    forcedYear?: number,
  ): [Observable<KPI.RoadmapIndicatorNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getRoadmapIndicatorsNotations(
      forcedPerimeters,
      forcedYear,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.roadmap_indicator === roadmapIndicator)),
      ),
      loading$,
    ];
  }

  public getChaptersNotations(
    roadmapIndicator: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.ChapterNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.chaptersNotations[key]) {
      this.chaptersNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.ChapterNotation[] = [];

      this.chaptersNotations[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.yearsService.startYear$,
        this.notationsRefresh$,
      ).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.chaptersNotationsLoading[key].next(true)),
        switchMap(([perimeters, startYear]) =>
          this.api.roadmap_notations_chapters(
            roadmapIndicator,
            {
              year: startYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
            },
          ).pipe(
            catchError(this.handleError),
            finalize(() => this.chaptersNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.chaptersNotations[key], this.chaptersNotationsLoading[key]];
  }

  public getChapterNotation(
    chapter: number,
    roadmapIndicator: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.ChapterNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getChaptersNotations(
      roadmapIndicator,
      forcedPerimeters,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.chapter === chapter)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getControlPointsNotations(
    roadmapIndicator: number,
    chapter: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.ControlPointNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.controlPointsNotations[key]) {
      this.controlPointsNotationsLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.ControlPointNotation[] = [];

      this.controlPointsNotations[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.yearsService.startYear$,
        this.notationsRefresh$,
      ).pipe(
        tap(() => accumulator = []),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.controlPointsNotationsLoading[key].next(true)),
        switchMap(([perimeters, startYear]) =>
          this.api.roadmap_notations_control_points(
            roadmapIndicator,
            chapter,
            {
              year: startYear,
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
            },
          ).pipe(
            catchError(this.handleError),
            finalize(() => this.controlPointsNotationsLoading[key].next(false)),
          ),
        ),
        map((notations) => {
          accumulator = [].concat(accumulator, notations);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.controlPointsNotations[key], this.controlPointsNotationsLoading[key]];
  }

  public getControlPointNotation(
    perimeter: number,
    chapter: number,
    roadmapIndicator: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.ControlPointNotation>, Observable<boolean>] {
    const [notations$, loading$] = this.getControlPointsNotations(
      roadmapIndicator,
      chapter,
      forcedPerimeters,
    );

    return [
      notations$.pipe(
        map((notations) => notations.find((notation) => notation.perimeter === perimeter)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  public getItemNotation(
    item: number,
    perimeter: number,
    chapter: number,
    roadmapIndicator: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<KPI.ItemNotation>, Observable<boolean>] {
    const [notation$, loading$] = this.getControlPointNotation(
      perimeter,
      chapter,
      roadmapIndicator,
      forcedPerimeters,
    );
    return [
      notation$.pipe(
        map((notation) => notation.items_notations.find((itemNotation) => itemNotation.item === item)),
        filter((notation) => notation !== undefined),
      ),
      loading$,
    ];
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }

  private getPerimetersObservable(perimeters?: KPI.Perimeter[]): Observable<KPI.Perimeter[]> {
    if (perimeters) {
      return of(perimeters).pipe(shareReplay(1));
    } else {
      return this.perimetersService.perimeters$;
    }
  }

  /**
   * If the notation doesn't exist for a specific year, the backend returns a 404.
   * In this case, catch it and return an empty array.
   * Otherwise, throw it.
   */
  private handleError(error: HttpErrorResponse): Observable<KPI.BaseRoadmapNotation[]> {
    if (error.status === 404) {
      return of([]);
    }
    return throwError(error);
  }

}
